import { useLayoutEffect, useRef, useState, CSSProperties } from 'react';
import { ThemeLogo, ThemeLogoProps } from '../ThemeLogo';
import { hasLockupInsets, resolveIconDropLockupInsets } from './iconDropLockupPad';
import {
  applyAdminInsets,
  ContentBbox,
  CssTrimFractions,
  findContentBbox,
  isBboxWorthTrimming,
  resolveCssTrim,
  resolveKnownLogoTrim,
  resolveTrimmedSlotStyle,
} from './trimLogoCanvas';

const readBboxFromImage = (img: HTMLImageElement): ContentBbox | null => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(img, 0, 0);
    const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
    return findContentBbox(data, canvas.width, canvas.height);
  } catch {
    return null;
  }
};

const resolveLockupBbox = (img: HTMLImageElement, src: string): ContentBbox => {
  const { naturalWidth, naturalHeight } = img;
  const measured = readBboxFromImage(img);
  const known = resolveKnownLogoTrim(src, naturalWidth, naturalHeight);
  return measured ?? known ?? { x: 0, y: 0, width: naturalWidth, height: naturalHeight };
};

/**
 * Play Again lockup only. Crops empty canvas at display time; ThemeLogo elsewhere is unchanged.
 */
export const IconDropTrimmedLogo = ({ className, style, kind, theme, ...rest }: ThemeLogoProps) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [crop, setCrop] = useState<CssTrimFractions | null>(null);
  const insets = resolveIconDropLockupInsets(
    theme as unknown as Record<string, unknown>,
    kind === 'studio' ? 'studio' : 'game',
  );
  const src = kind === 'studio' ? theme.studioLogo : kind === 'game' ? theme.gameLogo : undefined;

  useLayoutEffect(() => {
    const img = wrapRef.current?.querySelector('img');
    if (!img || !src) return;

    const apply = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      const bbox = applyAdminInsets(
        resolveLockupBbox(img, img.currentSrc || src),
        img.naturalWidth,
        img.naturalHeight,
        insets,
      );
      if (!isBboxWorthTrimming(bbox, img.naturalWidth, img.naturalHeight) && !hasLockupInsets(insets)) {
        setCrop(null);
        return;
      }
      setCrop(resolveCssTrim(bbox, img.naturalWidth, img.naturalHeight));
    };

    img.addEventListener('load', apply);
    if (img.complete) apply();
    return () => {
      img.removeEventListener('load', apply);
    };
  }, [src, insets.top, insets.right, insets.bottom, insets.left, kind, theme.studioLogo, theme.gameLogo]);

  const slot = crop ? resolveTrimmedSlotStyle(crop, style ?? {}) : null;
  const wrapperStyle: CSSProperties = slot
    ? {
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        ...slot,
      }
    : { display: 'inline-block', lineHeight: 0 };
  const imgStyle: CSSProperties = crop
    ? {
        position: 'absolute',
        left: crop.left,
        top: crop.top,
        width: crop.width,
        height: crop.height,
        maxWidth: 'none',
        maxHeight: 'none',
        minWidth: 0,
        objectFit: 'fill',
      }
    : (style ?? {});

  return (
    <div
      ref={wrapRef}
      className={className}
      style={wrapperStyle}
      data-lockup-aspect={crop ? crop.bboxWidth / crop.bboxHeight : undefined}
    >
      <ThemeLogo kind={kind} theme={theme} style={imgStyle} {...rest} />
    </div>
  );
};
